/**
 * The open times piece: Book a time / Open times. One service at a time: the
 * next four days with openings and up to four times each, in the visitor's own
 * time zone. Every time opens that service's page with the time picked. The
 * service list shows the most booked first, a few at a time, with arrows for
 * the rest (Gabriel 2026-10-20). Draws nothing when the studio lists no service.
 */
import type { SiteServiceSummaryDto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'open-times',
  description:
    "One service at a time: its next four days with openings, up to four times each, in the visitor's time zone; each time opens the service's page with it picked.",
  placeable: true,
  options: {
    eyebrow: { type: 'string', default: 'Book a time', maxLength: 60, description: 'The small label above the heading; null for none.' },
    heading: { type: 'string', default: 'Open times', maxLength: 80, description: 'The section heading; null for none.' },
    intro: {
      type: 'string',
      default: 'The next free times for each service. Choose a time to book it.',
      maxLength: 240,
      description: 'The line under the heading; null for none.',
    },
    perView: {
      type: 'number',
      min: 1,
      max: 20,
      default: 5,
      description: 'How many services the list shows at once; arrows page through the rest.',
    },
    note: {
      type: 'string',
      default: 'The time is held once you sign in with Telegram to confirm.',
      maxLength: 160,
      description: 'The small line beside See every open time; null for none. The default, about signing in, shows to a visitor only.',
    },
  },
  parts: {
    section: { description: 'The whole section.', required: false },
    eyebrow: { description: 'The small label above the heading.', required: false },
    heading: { description: 'The section heading.', required: false, role: 'title' },
    intro: { description: 'The line under the heading.', required: false },
    tabs: { description: 'The list of services to choose from.', required: false },
    tab: { description: 'One service to choose.', required: false, states: ['selected'] },
    pager: { description: 'The arrows and the count under the list, when it holds more services than show at once.', required: false },
    previous: { description: 'The arrow to the services before these.', required: false, states: ['disabled'] },
    next: { description: 'The arrow to more services.', required: false, states: ['disabled'] },
    range: { description: 'Which services show, as "1 to 5 of 12".', required: false },
    service: { description: "The chosen service's name and length.", required: false },
    zone: { description: "The visitor's time zone, named in words.", required: false },
    days: { description: 'The days with openings.', required: false },
    day: { description: 'One day and its times.', required: false },
    dayName: { description: 'Today, Tomorrow or the weekday and date.', required: false },
    dayDate: { description: 'The full date of the day.', required: false },
    time: { description: "One open time; opens the service's page with it picked.", required: true, role: 'time' },
    empty: { description: 'The line shown when the service has no open times coming up.', required: false },
    seeAll: { description: "The link to the service's page, with every open time.", required: false },
    note: { description: 'The small line beside See every open time.', required: false },
  },
  actions: {
    selectService: { description: 'Shows the open times of another listed service.', args: [{ name: 'serviceId', kind: 'item', list: 'services', key: 'id' }] },
    openTime: { description: "Opens the chosen service's page with this time picked.", args: [{ name: 'startsAt', kind: 'item', list: 'days[].times', key: 'startsAt' }] },
    openService: { description: "Opens the chosen service's page.", args: [] },
    previous: { description: 'Shows the services before the ones in view.', args: [] },
    next: { description: 'Shows the next services in the list.', args: [] },
  },
  events: {
    serviceSelected: 'A visitor chose a service.',
    timeOpened: 'A visitor chose an open time.',
    serviceOpened: "A visitor opened the service's page from See every open time.",
    paged: 'A visitor moved the service list with an arrow.',
  },
});

/** One open time, ready to draw. */
export interface OpenTimesSlot {
  /** The start, a UTC instant (ISO 8601). */
  readonly startsAt: string;
  /** The start in the visitor's zone: "11:15 AM". */
  readonly label: string;
  /** The service page with this time picked: `<service path>?start=<UTC ISO>`. */
  readonly href: string;
}

/** One day with openings, in the visitor's zone. */
export interface OpenTimesDay {
  /** The visitor's calendar day, YYYY-MM-DD. */
  readonly date: string;
  /** Today, Tomorrow, or the weekday and day number ("Sat 17"). */
  readonly name: string;
  /** The full date: "Fri, Oct 16". */
  readonly dateLabel: string;
  /** Up to four open times, earliest first. */
  readonly times: readonly OpenTimesSlot[];
}

/** What the open times piece knows. */
export interface OpenTimesState {
  /** The services' read: loading until the first read lands; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The listed services, in the studio's order; the section drops out when empty. */
  readonly services: readonly SiteServiceSummaryDto[];
  /** The service whose times show, or null while none is listed. */
  readonly selected: SiteServiceSummaryDto | null;
  /** The chosen service's open times: loading, ready (maybe with no days), or error. */
  readonly timesStatus: 'loading' | 'ready' | 'error';
  /** Up to four days with openings; empty when the service has none coming up. */
  readonly days: readonly OpenTimesDay[];
  /** The services the list shows now, at most the perView option: the most booked in the last 90 days first, the rest in the studio's order. */
  readonly shown: readonly SiteServiceSummaryDto[];
  /** Which group of the list is in view, from 0. */
  readonly page: number;
  /** How many groups the list makes; 1 when every service shows at once. */
  readonly pageCount: number;
  /** The position of the first shown service in the whole list, from 1; 0 when none is listed. */
  readonly firstShown: number;
  /** Whether an earlier group of services exists. */
  readonly canPrevious: boolean;
  /** Whether a later group of services exists. */
  readonly canNext: boolean;
  /** The visitor's device time zone (IANA). */
  readonly timezone: string;
  /** That zone in words: "Eastern Time (EDT)". */
  readonly zoneName: string;
  /** True for a signed-in client: the note beside See every open time is a visitor's only. */
  readonly signedIn: boolean;
}

/** What a drawing can ask the open times piece to do. */
export interface OpenTimesActions {
  /** Show the open times of the listed service with this id; any other id is ignored. */
  readonly selectService: (serviceId: string) => void;
  /** Open the chosen service's page with the time starting at this UTC instant picked. */
  readonly openTime: (startsAt: string) => void;
  /** Open the chosen service's page. */
  readonly openService: () => void;
  /** Show the group of services before this one; nothing when there is none. */
  readonly previous: () => void;
  /** Show the next group of services; nothing when there is none. */
  readonly next: () => void;
}

/** What the open times piece tells its host. */
export interface OpenTimesEvents {
  /** A visitor chose a service; carries its id. */
  readonly serviceSelected: { readonly serviceId: string };
  /** A visitor chose an open time. */
  readonly timeOpened: { readonly serviceId: string; readonly startsAt: string };
  /** A visitor opened the service's page from See every open time. */
  readonly serviceOpened: { readonly serviceId: string };
  /** A visitor moved the service list; carries the group now in view, from 0. */
  readonly paged: { readonly page: number };
}
